import React, { useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';
import SPADropdown from '@/components/admin/SysadminSPA/shared/SPADropdown';
import { useSPAToast } from '@/components/admin/SysadminSPA/shared/SPAToast';
import { useDeleteAlertMessage, usePublishAlertMessage } from './useAlertMessageMutations';
import type { AlertMessage } from './useContentAlertMessages';

interface AlertMessageActionsProps {
  alertMessage: AlertMessage;
}

type OpenDialog = 'delete' | 'preview' | null;

const errorMessage = (error: unknown, fallback: string) => {
  const errors = Axios.isAxiosError(error) ? error.response?.data?.errors : undefined;

  return Array.isArray(errors) && errors.length > 0 ? errors.join(' ') : fallback;
};

const ErrorLine = ({ children }: { children: React.ReactNode }) => (
  <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">{children}</p>
);

// AIDEV-NOTE: Edit still opens the legacy form (the Froala editor and program/lesson pickers live
// there); preview, publish, and delete are handled here against plato_api. Edit sits inside the
// menu like the legacy admin tables' actions dropdowns, not as a standalone button.
const AlertMessageActions = ({ alertMessage }: AlertMessageActionsProps) => {
  const {
    content, id, published, title, typeLabel,
  } = alertMessage;
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const publish = usePublishAlertMessage();
  const destroy = useDeleteAlertMessage();
  const spaToast = useSPAToast();

  const togglePublish = () => publish.mutate({ id, publish: !published }, {
    onSuccess: updated => spaToast.success(`${updated.publish ? 'Published' : 'Unpublished'} “${title}”.`),
  });

  // The row unmounts with the cache patch, taking the dialog with it — no close needed on success.
  const confirmDelete = () => destroy.mutate(id, {
    onSuccess: () => spaToast.success(`Deleted “${title}”.`),
  });

  const handleDeleteOpenChange = (open: boolean) => {
    if (!open) destroy.reset();
    setOpenDialog(open ? 'delete' : null);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <div className="tw-flex tw-items-center">
        <SPADropdown>
          <SPADropdown.Trigger asChild>
            <SPAButton aria-label={`Actions for ${title}`} size="xs" variant="outline">
              Actions
              <Icon icon="ChevronDown" size={12} />
            </SPAButton>
          </SPADropdown.Trigger>
          <SPADropdown.Content className="tw-w-44">
            <SPADropdown.Item asChild>
              <a href={Routes.edit_admin_alert_message_path(id)}>Edit</a>
            </SPADropdown.Item>
            <SPADropdown.Item onSelect={() => setOpenDialog('preview')}>Preview</SPADropdown.Item>
            <SPADropdown.Item disabled={!content || publish.isLoading} onSelect={togglePublish}>
              {published ? 'Unpublish' : 'Publish'}
              {!content && <span className="tw-ml-auto tw-text-xs tw-text-spa-tertiary">needs content</span>}
            </SPADropdown.Item>
            <SPADropdown.Separator />
            <SPADropdown.Item className="tw-text-spa-error" onSelect={() => setOpenDialog('delete')}>
              Delete
            </SPADropdown.Item>
          </SPADropdown.Content>
        </SPADropdown>
      </div>
      {publish.isError && (
        <ErrorLine>{errorMessage(publish.error, 'The alert message could not be updated. Try again.')}</ErrorLine>
      )}

      <SPADialog open={openDialog === 'preview'} onOpenChange={open => setOpenDialog(open ? 'preview' : null)}>
        <SPADialog.Content size="lg">
          <SPADialog.Header>
            <SPADialog.Title>{title}</SPADialog.Title>
            <SPADialog.Description>{typeLabel} preview</SPADialog.Description>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body>
            {content ? (
              // Teachers only ever see these on light pages, so the content sits on a white surface
              // whatever the SPA theme. box-border and m-0: the padding and Froala's 1px .fr-view
              // margin must not add to the body's width, or the body grows a horizontal scrollbar.
              <div
                className={cn(
                  'fr-view tw-box-border tw-m-0 tw-w-full tw-overflow-x-auto tw-rounded tw-p-6',
                  'tw-border tw-border-solid tw-border-spa-border tw-bg-white tw-text-neutral-900',
                )}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <p className="tw-m-0 tw-text-sm tw-text-spa-tertiary">This alert message has no content yet.</p>
            )}
          </SPADialog.Body>
        </SPADialog.Content>
      </SPADialog>

      <SPADialog open={openDialog === 'delete'} onOpenChange={handleDeleteOpenChange}>
        <SPADialog.Content size="sm">
          <SPADialog.Header>
            <SPADialog.Title>Delete alert message</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
            <SPADialog.Description>
              This permanently deletes “{title}”{published && ', which is currently published'}.
            </SPADialog.Description>
            {destroy.isError && (
              <ErrorLine>{errorMessage(destroy.error, 'The alert message could not be deleted. Try again.')}</ErrorLine>
            )}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={destroy.isLoading} variant="danger" onClick={confirmDelete}>
              Delete
            </SPAButton>
          </SPADialog.Footer>
        </SPADialog.Content>
      </SPADialog>
    </div>
  );
};

export default AlertMessageActions;
